import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addCalendarMonths, formatDayBR } from '@control-center/shared';

import { skipSubscriptionBilling } from '../../lib/subscriptions-api';

/** Status que aceitam pular cobrança — espelha ELIGIBLE_FOR_SKIP na API. */
const ELIGIBLE = ['ativa', 'em_teste'];

interface Props {
  subscriptionId: string;
  status: string;
  nextBillingDate: string | null;
  /** Dia combinado de cobrança — o servidor joga a data pra ele. */
  billingDay: number | null;
}

/**
 * Data DEPOIS do pulo — a MESMA conta do servidor (billing-skip.service):
 * +1 mês no dia combinado (DR-020 §1.5 revisada 2026-09-29). Antes a tela
 * somava 30 dias por conta própria e prometia uma data que o servidor não
 * aplicava; e como `next_billing_date` é dia puro, `toLocaleDateString`
 * ainda mostrava o dia anterior.
 */
function dateAfterSkip(nextBillingDate: string, billingDay: number | null): string {
  return addCalendarMonths(nextBillingDate, 1, billingDay ?? undefined);
}

/**
 * Pular a próxima cobrança deste cliente.
 *
 * Nasceu do resgate de mês grátis do afiliado, mas é uma ação de cortesia como
 * qualquer outra — antes disto não havia forma nenhuma, nem pela tela, de pular
 * a cobrança de alguém.
 *
 * A confirmação mostra a data ATUAL e a data DEPOIS: adiar cobrança é
 * irreversível pela tela, então o operador precisa ver o efeito antes.
 */
export function SkipBillingButton({
  subscriptionId,
  status,
  nextBillingDate,
  billingDay,
}: Props) {
  const [confirming, setConfirming] = useState(false);
  const queryClient = useQueryClient();

  const eligible = ELIGIBLE.includes(status) && Boolean(nextBillingDate);

  const mutation = useMutation({
    mutationFn: () => skipSubscriptionBilling(subscriptionId),
    onSuccess: (res) => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
      if (!res.applied) {
        // Motivo tipado da API — nunca esconder por que não deu.
        const motivos: Record<string, string> = {
          no_billing: 'Este cliente não tem cobrança gerenciada aqui.',
          not_eligible: 'O status atual não permite pular cobrança.',
          ambiguous: 'Há mais de uma assinatura cobrável — resolva manualmente.',
          version_conflict: 'A cobrança foi alterada em paralelo. Tente de novo.',
          already_applied: 'Esta cobrança já foi pulada hoje.',
        };
        window.alert(motivos[res.reason ?? ''] ?? 'Não foi possível pular a cobrança.');
      }
    },
    onError: () => {
      setConfirming(false);
      window.alert('Não foi possível pular a cobrança. Tente novamente.');
    },
  });

  if (!eligible) {
    return (
      <button
        type="button"
        disabled
        title={
          nextBillingDate
            ? `Indisponível: status "${status}" não permite pular cobrança`
            : 'Indisponível: esta assinatura não tem cobrança recorrente'
        }
        className="min-h-[44px] cursor-not-allowed rounded-[10px] border border-line px-3 text-[13px] text-muted-3 opacity-60 lg:min-h-[36px]"
      >
        Pular cobrança
      </button>
    );
  }

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="min-h-[44px] rounded-[10px] border border-line px-3 text-[13px] hover:bg-panel-2 lg:min-h-[36px]"
      >
        Pular cobrança
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-[10px] border border-line bg-panel-2 p-2">
      <span className="text-[13px]">
        Cobrança passa de <strong>{formatDayBR(nextBillingDate)}</strong> para{' '}
        <strong>
          {nextBillingDate ? formatDayBR(dateAfterSkip(nextBillingDate, billingDay)) : '—'}
        </strong>
        .
      </span>
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="min-h-[44px] rounded-[10px] bg-accent px-3 text-[13px] text-white disabled:opacity-60 lg:min-h-[36px]"
      >
        {mutation.isPending ? 'Pulando…' : 'Confirmar'}
      </button>
      <button
        type="button"
        onClick={() => setConfirming(false)}
        disabled={mutation.isPending}
        className="min-h-[44px] rounded-[10px] border border-line px-3 text-[13px] lg:min-h-[36px]"
      >
        Cancelar
      </button>
    </div>
  );
}
